import React, { useState, useEffect } from 'react'
import { Form, Button, Row, Col } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { deleteFile, saveFile } from '../../helpers/firebase'

/**
 * Modal de captura de accesorios, observación y fotos al ingresar un equipo.
 *
 * Fixes de la auditoría (spec items-adicionales-equipo-dependiente, grupo 40):
 *  - 40.1: State local (`accesorios`, `observacion`, `fotos`) — antes se MUTABA
 *          directamente `equnico` rompiendo la reactividad de React.
 *  - 40.2: `deleteImage` usaba `array.splice(idx, 1)` que retorna los eliminados,
 *          NO el nuevo array — el state se sobreescribía con basura. Ahora usa
 *          `filter` y `deleteFile` se llama antes del setState.
 *  - 40.3: `closeModal` en Equiposot eliminaba TODAS las fotos sin preguntar.
 *          Este componente ya no muta `equnico.fotos`; el manejo del "descarte"
 *          queda a cargo del contenedor (que pide confirmación).
 */
export const Accesorios = ({ equnico, Eqselect, setEqselect, setEstadomodal, onIngresado }) => {
  const [accesorios, setAccesorios] = useState(equnico?.accesorios || 'NINGUNO')
  const [observacion, setObservacion] = useState(equnico?.observacion || '')
  const [fotos, setFotos] = useState(Array.isArray(equnico?.fotos) ? equnico.fotos : [])
  const [subiendo, setSubiendo] = useState(false)

  // Re-sync cuando cambia el equipo mostrado (evita quedar con datos del anterior).
  useEffect(() => {
    setAccesorios(equnico?.accesorios || 'NINGUNO')
    setObservacion(equnico?.observacion || '')
    setFotos(Array.isArray(equnico?.fotos) ? equnico.fotos : [])
  }, [equnico?._id])

  const deleteImage = async (img) => {
    try {
      await deleteFile(img)
      setFotos((prev) => prev.filter((f) => f !== img))
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo eliminar la foto', text: err?.message || '' })
    }
  }

  const onUpload = async (event) => {
    if (!event.target?.files?.[0]) return
    try {
      setSubiendo(true)
      const link = await saveFile(event.target, 'images')
      setFotos((prev) => [...prev, link])
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo subir la foto', text: err?.message || '' })
    } finally {
      setSubiendo(false)
    }
  }

  const ingresar = () => {
    // Empujar el equipo enriquecido con los datos capturados (sin mutar equnico).
    const equipoEnriquecido = { ...equnico, accesorios, observacion, fotos: [...fotos] }
    setEqselect([...Eqselect, equipoEnriquecido])
    // Callback opcional que recibe el equipo enriquecido — evita el race
    // condition de leer `Eqselect` después de un setState pendiente. El contenedor
    // lo usa para detectar padre y encadenar el modal de sincronización dependiente.
    if (typeof onIngresado === 'function') {
      onIngresado(equipoEnriquecido)
    } else {
      setEstadomodal(false)
    }
  }

  return (
    <div>
      <Row className="g-2">
        <Col xs={12}>
          <Form.Label className="fw-semibold">Accesorios</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={accesorios}
            onChange={(e) => setAccesorios(e.target.value)}
            placeholder="Ej: cable de poder, sonda, estuche…"
          />
        </Col>
        <Col xs={12}>
          <Form.Label className="fw-semibold">Observación</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            placeholder="Estado del equipo al ingresar (rayones, faltantes, etc.)"
          />
        </Col>
        <Col xs={12}>
          <Form.Label className="fw-semibold">Fotos del equipo</Form.Label>
          <Form.Control
            type="file"
            accept="image/*"
            name="img"
            onChange={onUpload}
            disabled={subiendo}
          />
        </Col>
      </Row>

      {fotos.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mt-3">
          {fotos.map((img) => (
            <div key={img} className="position-relative">
              <i
                onClick={() => deleteImage(img)}
                className="bi bi-x-circle-fill text-danger position-absolute"
                style={{ top: 2, right: 2, cursor: 'pointer', background: 'white', borderRadius: '50%' }}
                title="Eliminar foto"
              />
              <img
                src={img}
                alt="foto equipo"
                style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #dee2e6' }}
              />
            </div>
          ))}
        </div>
      )}

      <div className="d-flex justify-content-end mt-3">
        <Button variant="primary" onClick={ingresar} disabled={subiendo}>
          <i className="bi bi-check-lg me-1" />
          Seleccionar
        </Button>
      </div>
    </div>
  )
}
